import React, { useContext } from 'react'
import { useGlobals, plannerClient } from 'context'
import { useQuery, useMutation } from '@apollo/client'
import { GET_PLAN_LOCATIONS } from '../queries'
import { CHANGE_PLAN_LOCATION, REMOVE_PLAN_LOCATION } from '../mutations'
import { Trans } from '@lingui/macro'

const PlanLocationContext = React.createContext()

function PlanLocationProvider({ children }) {
  const { createError, createSuccess } = useGlobals()
  const { loading, data, refetch } = useQuery(GET_PLAN_LOCATIONS)
  const { planLocations = [] } = data || {}
  const newUpdate = (cache, { data: { changePlanLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_PLAN_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_PLAN_LOCATIONS,
        data: { planLocations: [...cached.planLocations, changePlanLocation] },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const changeUpdate = (cache, { data: { changePlanLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_PLAN_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_PLAN_LOCATIONS,
        data: {
          planLocations: cached.planLocations.map((location) => {
            if (
              location.plan_location_id === changePlanLocation.plan_location_id
            ) {
              return changePlanLocation
            }
            return location
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const removeUpdate = (cache, { data: { removePlanLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_PLAN_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_PLAN_LOCATIONS,
        data: {
          planLocations: cached.planLocations.filter(
            (location) =>
              location.plan_location_id !== removePlanLocation.plan_location_id,
          ),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const [newLocation] = useMutation(CHANGE_PLAN_LOCATION, {
    update: newUpdate,
  })
  const [updateLocation] = useMutation(CHANGE_PLAN_LOCATION, {
    update: changeUpdate,
  })
  const [removeLocation] = useMutation(REMOVE_PLAN_LOCATION, {
    update: removeUpdate,
  })

  const hasLocationWithCoordinates =
    !loading &&
    planLocations.some(
      (location) =>
        location.longitude !== '0' &&
        location.latitude !== '0' &&
        location.longitude !== null &&
        location.latitude !== null &&
        location.longitude !== '' &&
        location.latitude !== '',
    )

  async function createPlanLocation(input) {
    try {
      await newLocation({
        variables: {
          input,
        },
        client: plannerClient,
      })
      createSuccess(<Trans>Location was created</Trans>)
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to create location</Trans>,
      )
    }
  }

  async function updatePlanLocation(input) {
    try {
      await updateLocation({
        variables: {
          input,
        },
        client: plannerClient,
      })
      createSuccess(<Trans>Location was updated</Trans>)
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to update location</Trans>,
      )
    }
  }

  async function removePlanLocation(plan_location_id) {
    try {
      await removeLocation({
        variables: {
          plan_location_id,
        },
        client: plannerClient,
      })
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to remove location</Trans>,
      )
    }
  }

  return (
    <PlanLocationContext.Provider
      value={{
        loading,
        refetch,
        planLocations,
        hasLocationWithCoordinates,
        createPlanLocation,
        updatePlanLocation,
        removePlanLocation,
      }}
    >
      {children}
    </PlanLocationContext.Provider>
  )
}

function usePlanLocations() {
  return useContext(PlanLocationContext)
}

export { PlanLocationProvider, usePlanLocations }
